// ---------------------------------------------------------------------------
// UBO Declaration Form — Add/remove UBOs with validation
// ---------------------------------------------------------------------------

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeclaredUbo {
  /** Client-side key for React rendering. */
  key: string;
  name: string;
  nationality: string;
  dateOfBirth: string;
  ownershipPercentage: number;
}

interface UboDeclarationFormProps {
  /** Current list of declared UBOs. */
  ubos: DeclaredUbo[];
  /** Callback when the UBO list changes. */
  onChange: (ubos: DeclaredUbo[]) => void;
  /** Minimum ownership percentage threshold to display guidance. */
  threshold?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateKey(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function createEmptyUbo(): DeclaredUbo {
  return {
    key: generateKey(),
    name: '',
    nationality: '',
    dateOfBirth: '',
    ownershipPercentage: 0,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UboDeclarationForm({
  ubos,
  onChange,
  threshold = 25,
}: UboDeclarationFormProps) {
  const { t } = useTranslation('portal');
  const totalPercentage = ubos.reduce(
    (sum, u) => sum + (u.ownershipPercentage || 0),
    0,
  );

  const handleAdd = useCallback(() => {
    onChange([...ubos, createEmptyUbo()]);
  }, [ubos, onChange]);

  const handleRemove = useCallback(
    (key: string) => {
      onChange(ubos.filter((u) => u.key !== key));
    },
    [ubos, onChange],
  );

  const handleFieldChange = useCallback(
    (key: string, field: keyof DeclaredUbo, value: string | number) => {
      onChange(
        ubos.map((u) => (u.key === key ? { ...u, [field]: value } : u)),
      );
    },
    [ubos, onChange],
  );

  const percentageExceeds100 = totalPercentage > 100;
  const hasIncomplete = ubos.some(
    (u) => !u.name.trim() || u.ownershipPercentage <= 0,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-ink">
            {t('uboDeclaration.heading')}
          </h3>
          <p className="mt-0.5 text-xs text-ink-muted">
            {t('uboDeclaration.description', { threshold })}
          </p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center gap-1 rounded-lg border border-hairline-strong px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-surface-sunken transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t('uboDeclaration.addUbo')}
        </button>
      </div>

      {/* Total percentage indicator */}
      <div
        className={`rounded-lg px-4 py-2 text-sm ${
          percentageExceeds100
            ? 'bg-danger-tint border border-danger/30 text-danger'
            : totalPercentage > 0
              ? 'bg-primary-tint border border-primary/30 text-primary-hover'
              : 'bg-surface-sunken border border-hairline text-ink-muted'
        }`}
      >
        {t('uboDeclaration.totalLabel')}<strong>{totalPercentage.toFixed(1)}%</strong>
        {percentageExceeds100 && (
          <span className="ms-2 text-xs">
            {t('uboDeclaration.exceedsHint')}
          </span>
        )}
      </div>

      {/* UBO entries */}
      {ubos.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-hairline-strong p-6 text-center text-sm text-ink-muted">
          {t('uboDeclaration.emptyState')}
        </div>
      ) : (
        <div className="space-y-4">
          {ubos.map((ubo, index) => (
            <div
              key={ubo.key}
              className="rounded-lg border border-hairline bg-white p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-ink-muted">
                  {t('uboDeclaration.uboNumber', { number: index + 1 })}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemove(ubo.key)}
                  className="rounded p-1 text-ink-muted hover:bg-surface-sunken hover:text-danger transition-colors"
                  aria-label={t('uboDeclaration.removeAria', { number: index + 1 })}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label
                    htmlFor={`ubo-name-${ubo.key}`}
                    className="block text-xs font-medium text-ink-muted mb-1"
                  >
                    {t('uboDeclaration.fields.fullName')}
                  </label>
                  <input
                    id={`ubo-name-${ubo.key}`}
                    type="text"
                    value={ubo.name}
                    onChange={(e) =>
                      handleFieldChange(ubo.key, 'name', e.target.value)
                    }
                    placeholder={t('uboDeclaration.fields.fullNamePlaceholder')}
                    className="block w-full rounded-md border border-hairline-strong px-3 py-1.5 text-sm text-ink-muted focus:border-primary focus:ring-1 focus:ring-primary/20"
                  />
                </div>

                {/* Nationality */}
                <div>
                  <label
                    htmlFor={`ubo-nationality-${ubo.key}`}
                    className="block text-xs font-medium text-ink-muted mb-1"
                  >
                    {t('uboDeclaration.fields.nationality')}
                  </label>
                  <input
                    id={`ubo-nationality-${ubo.key}`}
                    type="text"
                    value={ubo.nationality}
                    onChange={(e) =>
                      handleFieldChange(ubo.key, 'nationality', e.target.value)
                    }
                    placeholder={t('uboDeclaration.fields.nationalityPlaceholder')}
                    className="block w-full rounded-md border border-hairline-strong px-3 py-1.5 text-sm text-ink-muted focus:border-primary focus:ring-1 focus:ring-primary/20"
                  />
                </div>

                {/* Date of Birth */}
                <div>
                  <label
                    htmlFor={`ubo-dob-${ubo.key}`}
                    className="block text-xs font-medium text-ink-muted mb-1"
                  >
                    {t('uboDeclaration.fields.dateOfBirth')}
                  </label>
                  <input
                    id={`ubo-dob-${ubo.key}`}
                    type="date"
                    value={ubo.dateOfBirth}
                    onChange={(e) =>
                      handleFieldChange(ubo.key, 'dateOfBirth', e.target.value)
                    }
                    className="block w-full rounded-md border border-hairline-strong px-3 py-1.5 text-sm text-ink-muted focus:border-primary focus:ring-1 focus:ring-primary/20"
                  />
                </div>

                {/* Ownership Percentage */}
                <div className="sm:col-span-2">
                  <label
                    htmlFor={`ubo-pct-${ubo.key}`}
                    className="block text-xs font-medium text-ink-muted mb-1"
                  >
                    {t('uboDeclaration.fields.ownershipPercentage')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id={`ubo-pct-${ubo.key}`}
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={ubo.ownershipPercentage || ''}
                      onChange={(e) =>
                        handleFieldChange(
                          ubo.key,
                          'ownershipPercentage',
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder={t('uboDeclaration.fields.ownershipPlaceholder')}
                      className="block w-32 rounded-md border border-hairline-strong px-3 py-1.5 text-sm text-ink-muted focus:border-primary focus:ring-1 focus:ring-primary/20"
                    />
                    <span className="text-sm text-ink-muted">%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Validation messages */}
      {hasIncomplete && ubos.length > 0 && (
        <p className="text-xs text-warning">
          {t('uboDeclaration.incompleteWarning')}
        </p>
      )}
    </div>
  );
}
